import axios from "axios";
import { USER_ENDPOINTS, API_BASE_URL } from "../config/apiConfig";
import { createAuthAxios, handleApiError } from "../utils/apiUtils";

// Request debounce cache
const requestCache = {
  getNotifications: {
    timestamp: 0,
    promise: null,
    data: null,
    ttl: 10000, // 10 seconds cache TTL
  },
};

// Debounce API requests to prevent excessive calls
const debounce = (func, delay) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    return new Promise((resolve) => {
      timeout = setTimeout(() => {
        resolve(func(...args));
      }, delay);
    });
  };
};

/**
 * Format a timestamp to a relative time string (e.g. "2 hours ago")
 * @param {string} timestamp - ISO timestamp string
 * @returns {string} Relative time string
 */
const formatTimestamp = (timestamp) => {
  if (!timestamp) return "";

  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  const diffWeek = Math.floor(diffDay / 7);
  const diffMonth = Math.floor(diffDay / 30);
  const diffYear = Math.floor(diffDay / 365);

  if (diffSec < 60) return "just now";
  if (diffMin < 60) return `${diffMin} minute${diffMin !== 1 ? "s" : ""} ago`;
  if (diffHour < 24) return `${diffHour} hour${diffHour !== 1 ? "s" : ""} ago`;
  if (diffDay < 7) return `${diffDay} day${diffDay !== 1 ? "s" : ""} ago`;
  if (diffWeek < 4) return `${diffWeek} week${diffWeek !== 1 ? "s" : ""} ago`;
  if (diffMonth < 12)
    return `${diffMonth} month${diffMonth !== 1 ? "s" : ""} ago`;
  return `${diffYear} year${diffYear !== 1 ? "s" : ""} ago`;
};

/**
 * Service for notification-related API operations
 */
const notificationService = {
  /**
   * Get all notifications for the current user
   * @param {number} page - Page number for pagination
   * @param {number} limit - Number of notifications per page
   * @param {boolean} unreadOnly - Whether to fetch only unread notifications
   * @returns {Promise<Object>} Notifications data with pagination info
   */
  getNotifications: async (page = 1, limit = 20, unreadOnly = false) => {
    try {
      // Use cache for repeated calls with the same parameters (especially for unread counts)
      const cacheKey = `${page}_${limit}_${unreadOnly}`;
      const now = Date.now();

      // Return cached data if it's fresh and parameters match
      if (
        requestCache.getNotifications.promise &&
        requestCache.getNotifications.cacheKey === cacheKey &&
        now - requestCache.getNotifications.timestamp <
          requestCache.getNotifications.ttl
      ) {
        return await requestCache.getNotifications.promise;
      }

      // Create an authenticated axios instance
      const authAxios = createAuthAxios();

      // Construct the endpoint path
      const endpoint = "/notifications";

      // Create the promise for this request
      const requestPromise = authAxios
        .get(endpoint, {
          params: {
            page,
            limit,
            unreadOnly: unreadOnly ? "true" : "false",
          },
          // Add timeout to prevent hanging requests
          timeout: 10000,
        })
        .then((response) => {
          // Format timestamps for display
          if (response.data.notifications) {
            response.data.notifications = response.data.notifications.map(
              (notification) => ({
                ...notification,
                timestamp: formatTimestamp(notification.createdAt),
              })
            );
          }

          // Update cache
          requestCache.getNotifications.data = response.data;
          return response.data;
        });

      // Store in cache
      requestCache.getNotifications.promise = requestPromise;
      requestCache.getNotifications.timestamp = now;
      requestCache.getNotifications.cacheKey = cacheKey;

      return await requestPromise;
    } catch (error) {
      const errorData = handleApiError(error);

      // Invalidate cache on error
      if (requestCache.getNotifications.cacheKey) {
        requestCache.getNotifications.timestamp = 0;
      }

      // Provide fallback data for UI in case of network errors
      if (
        error.message === "Network Error" &&
        requestCache.getNotifications.data
      ) {
        return {
          ...requestCache.getNotifications.data,
          isStale: true,
        };
      }

      throw errorData;
    }
  },

  /**
   * Get unread notification count
   * @returns {Promise<Object>} Object with unreadCount property
   */
  getUnreadCount: async () => {
    try {
      // Use a short cache TTL for unread counts to ensure freshness
      const now = Date.now();
      const cacheKey = "unread_count";

      // Return cached data if it's fresh (only cache for 30 seconds)
      if (
        requestCache.getUnreadCount &&
        requestCache.getUnreadCount.timestamp &&
        now - requestCache.getUnreadCount.timestamp < 30000
      ) {
        return requestCache.getUnreadCount.data;
      }

      // Create an authenticated axios instance
      const authAxios = createAuthAxios();

      // Make a dedicated API call for just the unread count
      const endpoint = "/notifications/unread-count";

      const requestPromise = authAxios
        .get(endpoint, { timeout: 5000 }) // Short timeout for quick response
        .then((response) => {
          // Update cache
          if (!requestCache.getUnreadCount) requestCache.getUnreadCount = {};
          requestCache.getUnreadCount.data = response.data;
          requestCache.getUnreadCount.timestamp = now;
          return response.data;
        });

      return await requestPromise;
    } catch (error) {
      const errorData = handleApiError(error);

      // Provide fallback data if we have cached data
      if (requestCache.getUnreadCount && requestCache.getUnreadCount.data) {
        return requestCache.getUnreadCount.data;
      }

      // Default fallback
      return { unreadCount: 0 };
    }
  },

  /**
   * Mark a notification as read
   * @param {string} id - Notification ID
   * @returns {Promise<Object>} Updated notification
   */
  markAsRead: debounce(async (id) => {
    try {
      // Validate input
      if (!id) {
        throw new Error("Notification ID is required");
      }

      const authAxios = createAuthAxios();
      const endpoint = `/notifications/${id}/read`;
      const response = await authAxios.put(endpoint);

      // Invalidate notifications cache
      requestCache.getNotifications.timestamp = 0;

      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
      throw errorData;
    }
  }, 300), // 300ms debounce

  /**
   * Mark all notifications as read
   * @returns {Promise<Object>} Result
   */
  markAllAsRead: async () => {
    try {
      const authAxios = createAuthAxios();
      const endpoint = "/notifications/read-all";
      const response = await authAxios.put(endpoint);

      // Invalidate notifications cache
      requestCache.getNotifications.timestamp = 0;

      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
      throw errorData;
    }
  },

  /**
   * Delete a notification
   * @param {string} id - Notification ID
   * @returns {Promise<Object>} Result
   */
  deleteNotification: debounce(async (id) => {
    try {
      // Validate input
      if (!id) {
        throw new Error("Notification ID is required");
      }

      const authAxios = createAuthAxios();
      const endpoint = `/notifications/${id}`;
      const response = await authAxios.delete(endpoint);

      // Invalidate notifications cache
      requestCache.getNotifications.timestamp = 0;

      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
      throw errorData;
    }
  }, 300), // 300ms debounce

  /**
   * Clear all notifications
   * @returns {Promise<Object>} Result
   */
  clearAllNotifications: async () => {
    try {
      const authAxios = createAuthAxios();
      const endpoint = "/notifications/clear-all";
      const response = await authAxios.delete(endpoint);

      // Invalidate notifications cache
      requestCache.getNotifications.timestamp = 0;

      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
      throw errorData;
    }
  },

  /**
   * Handle notification action
   * @param {string} id - Notification ID
   * @param {string} action - Action to perform
   * @returns {Promise<Object>} Result with possible redirect URL
   */
  handleNotificationAction: debounce(async (id, action) => {
    try {
      // Validate inputs
      if (!id) {
        throw new Error("Notification ID is required");
      }

      if (!action) {
        throw new Error("Action is required");
      }

      // Validate action type for security
      const validActions = [
        "accept_collaboration",
        "decline_collaboration",
        "view_task",
        "review_proposal",
      ];

      if (!validActions.includes(action)) {
        throw new Error(`Invalid action: ${action}`);
      }

      const authAxios = createAuthAxios();
      const endpoint = `/notifications/${id}/action`;
      const response = await authAxios.post(endpoint, { action });

      // Invalidate notifications cache
      requestCache.getNotifications.timestamp = 0;

      return response.data;
    } catch (error) {
      const errorData = handleApiError(error);
      throw errorData;
    }
  }, 300), // 300ms debounce

  /**
   * Get messaging unread count for sidebar badge
   * NEW: Fetches unread message count from messaging module
   * @returns {Promise<Object>} Messaging unread count and conversation preview
   */
  getMessagingUnreadCount: async () => {
    try {
      const authAxios = createAuthAxios();
      const endpoint = `${API_BASE_URL}/messaging/sidebar-unread`;
      const response = await authAxios.get(endpoint, {
        timeout: 10000,
      });

      return response.data;
    } catch (error) {
const errorData = handleApiError(error);
      throw errorData;
    }
  },
};

export default notificationService;
